import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  inject,
  Input,
  NgZone,
} from '@angular/core';
import { BuySummaryComponent } from './buy-summary/buy-summary.component';
import { DialogModule } from 'primeng/dialog';
import { AsyncPipe, NgIf, NgTemplateOutlet } from '@angular/common';
import { SharedModule } from 'primeng/api';
import {
  filter,
  map,
  share,
  shareReplay,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import { OrderformFascade } from '../../fascades/orderform.fascade';
import {
  isElementInViewport,
  viewportVisibilityChange,
} from '@digistore/ds24-ui/rxjs';
import {
  BehaviorSubject,
  combineLatest,
  firstValueFrom,
  Observable,
  of,
  ReplaySubject,
  Subject,
} from 'rxjs';
import { BuyButtonComponent } from './buy-button/buy-button.component';
import { BuyButtonSummarySetting } from './buy-button-wrapper.model';
import { Transaction } from '@elastic/apm-rum';
import * as OrderformActions from '../../ngrx/orderform.actions';
import { Store } from '@ngrx/store';
import {
  MonitoringService,
  OrderformState,
  OrderFormValidationResult,
} from '@orderform/widgets';
import { OrderformIframeService } from '../../services/orderform-iframe.service';
import { IframeModalService } from '../../components/iframe-modal/iframe-modal.service';

@Component({
  selector: 'orderform-buy-button-wrapper',
  templateUrl: './buy-button-wrapper.component.html',
  styleUrls: ['./buy-button-wrapper.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    BuySummaryComponent,
    DialogModule,
    NgTemplateOutlet,
    SharedModule,
    AsyncPipe,
    NgIf,
    BuyButtonComponent,
  ],
})
export class BuyButtonWrapperComponent implements AfterViewInit {
  @Input('buy-btn-theme') buyBtnTheme = null;
  @Input() buyBtnText = null;
  @Input() sealType: 'gold' | 'blue' | null = null;

  @Input() iconLeft = null;
  @Input() iconRight = null;

  private _summarySetting$ = new ReplaySubject<BuyButtonSummarySetting>(1);
  @Input() set summarySetting(setting: BuyButtonSummarySetting) {
    this._summarySetting$.next(setting);
  }
  @Input() summaryLayout: 'horizontal' | 'vertical' = 'horizontal';
  @Input() summaryOuterContainerClasses?: string;
  @Input() summaryInnerContainerClasses?: string;

  // null = not in editor
  private _forceShowSummary$ = new BehaviorSubject<'false' | 'true' | null>(
    null
  );
  @Input() set forceShowSummary(force: 'false' | 'true' | null) {
    this._forceShowSummary$.next(force);
  }

  /**
   * Attention - dies attribute will be set via javascript. Not via hbs template!
   */
  @Input() set forceShowModal(status: 'true' | 'false') {
    if (status) {
      this.modalVisible = status === 'true';
    }
  }

  @Input() set editorMode(value: string) {
    this._isTranslationMode = value === 'translation';
    this._isEditorMode = value === 'translation' || value === 'design';
  }

  get isTranslationMode(): boolean {
    return this._isTranslationMode;
  }

  private _isTranslationMode: boolean;
  private _isEditorMode = false;

  private _orderformFascade = inject(OrderformFascade);
  private _ngAfterViewInit$ = new ReplaySubject<void>(1);
  private _elRef = inject(ElementRef);
  private _blockButton$ = new BehaviorSubject(false);
  private _store: Store<OrderformState> = inject(Store<OrderformState>);
  private _ngZone = inject(NgZone);
  private _iframeService = inject(OrderformIframeService);
  private _iframeModal = inject(IframeModalService);
  private _monitoring = inject(MonitoringService);
  private _cdr = inject(ChangeDetectorRef);

  public modalVisible = false;
  /**
   * Summary setting
   */
  public readonly modalTitle = $localize`:@@buy_button.buy-verification.title:Sie bestellen`;
  public readonly modalTitleAsGift = $localize`:@@buy_button.buy-verification.title-as-gift:Sie bestellen als Geschenk`;

  public isGift$ = this._orderformFascade.order$.pipe(
    map((order) => {
      return !!order.order.custom_forms.is_gift;
    })
  );

  needToShowOrderSummaryBeforePurchaseByConfig$ =
    this._orderformFascade.config$.pipe(
      map((data) => {
        return data.show_order_summary_before_purchase;
      })
    );

  private _isOrderSummaryAndProductListInViewPort$ = combineLatest([
    this._ngAfterViewInit$.pipe(
      switchMap(() => viewportVisibilityChange(this._elRef.nativeElement, 100)),
      filter((buyButtonIsVisible) => buyButtonIsVisible),
      share()
    ),
    this._summarySetting$,
  ]).pipe(
    map(([, summarySetting]) => {
      const testToForce: BuyButtonSummarySetting[] = [
        'above_if_needed',
        'popup_if_needed',
      ];
      if (!testToForce.includes(summarySetting)) {
        return false;
      }
      return this._isOrderSummaryAndProductListInViewPort();
    })
  );

  needToShowOrderSummaryBeforePurchase$: Observable<boolean> = combineLatest([
    this.needToShowOrderSummaryBeforePurchaseByConfig$,
    this._isOrderSummaryAndProductListInViewPort$,
  ]).pipe(
    map(
      ([
        show_order_summary_before_purchase,
        forceAllowPurchaseWithoutSummary,
      ]) => {
        if (forceAllowPurchaseWithoutSummary) {
          return false;
        }
        return show_order_summary_before_purchase;
      }
    ),
    shareReplay(1)
  );

  showSummary$: Observable<boolean> = combineLatest([
    this._summarySetting$,
    this._forceShowSummary$,
    this.needToShowOrderSummaryBeforePurchase$,
  ]).pipe(
    map(([summarySetting, forceShowSummary, needOverViewBeforePurchase]) => {
      if (forceShowSummary) {
        return forceShowSummary === 'true';
      }

      if (summarySetting === 'above_always') {
        return true;
      }

      if (summarySetting === 'above_if_needed') {
        return needOverViewBeforePurchase;
      }

      if (summarySetting === 'popup_if_needed') {
        return false;
      }
    })
  );

  isCustomTextOnPayButtonAllowed$: Observable<boolean> =
    this._orderformFascade.config$.pipe(
      map((data) => data.is_pay_button_label_changed_allowed)
    );

  selectedPaymentMethod$ = this._orderformFascade.order$.pipe(
    map((e) => {
      return e.order.payment_provider;
    }),
    shareReplay(1)
  );

  buttonText$ = combineLatest([
    this.isCustomTextOnPayButtonAllowed$,
    this.selectedPaymentMethod$,
  ]).pipe(
    map(([customTextAllowed, selectedPaymentProv]) => {
      // void == free product
      if (selectedPaymentProv.pay_method === 'void') {
        return $localize`:@@orderform.get_now_for_free:Jetzt kostenlos sichern`;
      }

      // check is allowed to use custom button text
      if (customTextAllowed && this.buyBtnText) {
        return this.buyBtnText;
      } else {
        // use text of selected payment prov.
        // fallback customButtonText
        return (
          selectedPaymentProv.label_data?.default_submit_button.replace(
            '|',
            '<br/>'
          ) ||
          this.buyBtnText ||
          $localize`:@@orderform.buy_now:Jetzt kaufen`
        );
      }
    })
  );

  buttonDisabled$ = combineLatest([
    this._blockButton$,
    this.selectedPaymentMethod$,
  ]).pipe(
    map(([blockButton, selectedMethod]) => {
      return blockButton || (this._isEditorMode && selectedMethod.disabled);
    })
  );

  buttonTextBeforeOrderSummary$: Observable<string> =
    this._orderformFascade.config$.pipe(
      map((data) => data.payment_button_label_overwrite)
    );

  paymentLegalHint$ = combineLatest([
    this.buttonText$,
    this._orderformFascade.paymentLegalHint$,
  ]).pipe(
    map(([buttonText, hint]) =>
      hint ? hint.replace('__BUTTON__', buttonText) : null
    )
  );

  isButtonVisible$ = this.selectedPaymentMethod$.pipe(
    map(
      (selectedMethod) =>
        selectedMethod.is_submit_button_visible ||
        selectedMethod.pay_method === 'alias'
    )
  );

  ngAfterViewInit() {
    this._ngAfterViewInit$.next();
  }

  private _isOrderSummaryAndProductListInViewPort() {
    /**
     * Check only force buy if button is rendered once!
     */
    if (isElementInViewport(this._elRef.nativeElement, 0)) {
      const isElementVisible = (el): boolean => {
        return !!el.getBoundingClientRect().height;
      };

      const filterCond = (element: HTMLElement): boolean => {
        return isElementVisible(element) && isElementInViewport(element, 0);
      };

      // Alle relevanten Elemente der Seite durchgehen und prüfen, ob sie vollständig sichtbar sind
      const productListsEls = Array.from(
        document.getElementsByTagName('ds-orderform-product-list')
      ).filter(filterCond);

      const orderSummaryEls = Array.from(
        document.getElementsByTagName('ds-orderform-order-summary')
      ).filter(filterCond);

      if (orderSummaryEls.length && productListsEls.length) {
        return true;
      }
    }

    return false;
  }

  private async scrollToFormError() {
    await new Promise((resolve) => {
      this._ngZone.runOutsideAngular(() =>
        setTimeout(() => {
          const firstElement = document.querySelector(
            'orderform-input-errors .error, ds-orderform-buy-button .error'
          );
          if (firstElement) {
            firstElement.scrollIntoView({ block: 'center' });
            this._iframeService.scrollToIframeElementPosition(
              firstElement.getBoundingClientRect().top
            );
          }
          resolve(true);
        })
      );
    });
  }

  private async _trackValidationErrors(
    transaction: Transaction,
    validationResults: OrderFormValidationResult[],
    source: 'frontend' | 'backend'
  ) {
    validationResults.forEach((item) => {
      item.invalidFieldList.forEach((field) => {
        field.errors.forEach((err) => {
          const name =
            source + ':' + item.component + ':' + field.id + ':' + err;
          transaction.startSpan(
            name,
            'orderform-validation.' + source + '.' + item.component
          );
        });
      });
    });
  }

  @HostListener('click', ['$event'])
  public openLegalLink(e: Event) {
    const anchor = e.target as Element;
    const isInstallmentCalculaterButton = e.target['classList'].contains(
      'installment_calculator_button'
    );

    if (anchor.tagName !== 'A' || isInstallmentCalculaterButton) {
      return;
    }
    e.preventDefault();
    this._iframeModal.open(
      anchor.getAttribute('title') ?? anchor.textContent,
      anchor.getAttribute('href')
    );
  }

  public async _beforeSubmitFrontendValidation(nameOfTransaction: string) {
    const buyNowTransaction = this._monitoring.trackUserInteraction(
      nameOfTransaction,
      false
    );

    this._store.dispatch(OrderformActions.formValidation());
    const validationResult = await this._orderformFascade.formValidation.emit();

    if (
      !validationResult.reduce((valid, result) => valid && result.valid, true)
    ) {
      this._store.dispatch(OrderformActions.formValidationFailedBeforeSubmit());
      this._trackValidationErrors(
        buyNowTransaction,
        validationResult,
        'frontend'
      );
      await this.scrollToFormError();
      this._orderformFascade.submitFailed.emit();
      buyNowTransaction.end();
      return false;
    }

    this._store.dispatch(OrderformActions.formValidationSuccess());

    // Check Validations
    const canSubmit = await firstValueFrom(
      this._orderformFascade.beforeSubmitOrderForm()
    );

    if (!canSubmit) {
      await this.scrollToFormError();
      this._orderformFascade.submitFailed.emit();

      this._trackValidationErrors(
        buyNowTransaction,
        validationResult,
        'frontend'
      );
      buyNowTransaction.end();
      return false;
    }

    buyNowTransaction.end();
    return true;
  }

  public async showSummaryDisalog() {
    if (this._isEditorMode) {
      return;
    }

    /**
     * Block double clicks on buy button
     */
    this._blockButton$.next(true);

    this._monitoring.addLabels({ orderformSummary: 'dialog' });

    const valid = await this._beforeSubmitFrontendValidation('BuyShowSummary');
    this._blockButton$.next(false);

    if (!valid) {
      return;
    }

    /**
     * Modal for summary before purchase check
     */
    this.modalVisible = true;
  }

  public async submit(trackWithDialog = false) {
    if (this._isEditorMode) {
      return;
    }

    /**
     * Block double clicks on buy button
     */
    this._blockButton$.next(true);

    if (trackWithDialog) {
      this._monitoring.addLabels({ orderformSummary: 'dialog' });
    } else {
      const summaryShown = await firstValueFrom(
        this.needToShowOrderSummaryBeforePurchase$
      );
      this._monitoring.addLabels({
        orderformSummary: summaryShown ? 'above' : 'none',
      });
    }

    const valid = await this._beforeSubmitFrontendValidation('BuyButtonClick');

    if (!valid) {
      this._blockButton$.next(false);
      return;
    }

    // Close confirm for loading spinner
    this.modalVisible = false;
    this._cdr.detectChanges();

    const backendSubmitTransaction = this._monitoring.trackUserInteraction(
      'BuySubmit',
      false
    );
    const sendSuccess = await firstValueFrom(
      this._orderformFascade.submitOrderForm()
    );
    // unblock
    this._blockButton$.next(false);

    // Nachdem das Formular abgesendet wurde kann es im Backend noch zu Fehler gekommen sein
    const backendValidationResult =
      await this._orderformFascade.formValidation.emit();
    const formStillValid = backendValidationResult.reduce((valid, result) => {
      return valid && result.valid;
    }, true);

    if (!formStillValid) {
      this._trackValidationErrors(
        backendSubmitTransaction,
        backendValidationResult,
        'backend'
      );
      await this.scrollToFormError();
      this._orderformFascade.submitFailed.emit();
      this._store.dispatch(OrderformActions.formValidationFailedAfterSubmit());
      backendSubmitTransaction.end();
      return false;
    } else if (sendSuccess) {
      backendSubmitTransaction.end();
      this._orderformFascade.afterSubmit.emit().then((results) => {
        if (
          !results.reduce((isValid, result) => isValid && result.valid, true)
        ) {
          this._store.dispatch(OrderformActions.afterSubmitOrderFormFailed());
        }
      });
    } else {
      backendSubmitTransaction.end();
    }
  }
}
